import tippy from "tippy.js";
import {
  applyEditorLink,
  normalizeLinkHref,
  unsetEditorLink,
} from "./linkEditorUtils";

const LINK_POPUP_Z_INDEX = "100000";
const LINK_POPUP_STYLE_ID = "exiq-link-input-popup-styles";

const LINK_POPUP_STYLES = `
  .slash-link-input-popup {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 8px;
    box-shadow: 0 8px 16px rgba(16, 24, 40, 0.12);
  }
  .slash-link-input-popup .link-input-form {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }
  .slash-link-input-popup .link-input {
    width: 240px;
    min-height: 36px;
    padding: 8px 10px;
    border: 1px solid #2563eb;
    border-radius: 8px;
    font: inherit;
    box-sizing: border-box;
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .slash-link-input-popup .link-input:focus {
    border-color: #1d4ed8;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
  }
  .slash-link-input-popup .link-input--invalid {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
  }
  .slash-link-input-popup .link-input-error {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: #dc2626;
    white-space: nowrap;
  }
  .slash-link-input-popup .link-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: #f2f4f7;
    cursor: pointer;
  }
  [data-tippy-root]:has(.slash-link-input-popup) {
    z-index: ${LINK_POPUP_Z_INDEX} !important;
  }
`;

function ensureLinkPopupStyles() {
  if (typeof document === "undefined") {
    return;
  }

  let style = document.getElementById(LINK_POPUP_STYLE_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = LINK_POPUP_STYLE_ID;
    document.head.appendChild(style);
  }

  if (style.textContent !== LINK_POPUP_STYLES) {
    style.textContent = LINK_POPUP_STYLES;
  }
}

export function removeAllLinkPopupDomNodes() {
  if (typeof document === "undefined") {
    return;
  }

  document.querySelectorAll(".slash-link-input-popup").forEach((node) => {
    node.closest("[data-tippy-root]")?.remove();
  });
}

function focusLinkInput(input) {
  if (!input) {
    return;
  }

  window.requestAnimationFrame(() => {
    input.focus({ preventScroll: true });
    input.select();
  });
}

/**
 * Floating URL prompt for slash → Link and toolbar Link actions.
 * @param {import('@tiptap/core').Editor} editor
 * @param {() => DOMRect} getRect
 * @param {{ initialUrl?: string, allowUnset?: boolean, onSave?: (href: string|null) => void, onClose?: () => void }} [options]
 */
export function showLinkInputPopup(editor, getRect, options = {}) {
  if (!editor?.view) {
    return null;
  }

  ensureLinkPopupStyles();
  removeAllLinkPopupDomNodes();

  let popup = null;
  let isClosing = false;
  const component = document.createElement("div");
  component.className = "link-input-popup slash-link-input-popup";
  component.dataset.exiqManagedLinkPopup = "true";
  component.innerHTML = `
    <form class="link-input-form" novalidate>
      <input
        type="text"
        inputmode="url"
        class="link-input"
        placeholder="Paste a link..."
        aria-label="Link URL"
        aria-describedby="exiq-link-input-error"
        autocomplete="off"
      />
      <button type="submit" class="link-action-btn" aria-label="Apply link">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M2.5 8H13.5M13.5 8L9 3.5M13.5 8L9 12.5" stroke="#6b7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <p id="exiq-link-input-error" class="link-input-error" hidden>Enter a valid link (e.g. example.com)</p>
    </form>
  `;

  const form = component.querySelector("form");
  const input = component.querySelector("input");
  const errorMessage = component.querySelector(".link-input-error");

  component.addEventListener("mousedown", (event) => {
    event.preventDefault();
  });

  const closePopup = () => {
    if (isClosing) {
      return;
    }
    isClosing = true;

    if (popup && !popup.state?.isDestroyed) {
      popup.hide();
      popup.destroy();
    }

    popup = null;
    removeAllLinkPopupDomNodes();
    options.onClose?.();
  };

  const clearValidationError = () => {
    input.classList.remove("link-input--invalid");
    input.removeAttribute("aria-invalid");
    errorMessage.hidden = true;
  };

  const showValidationError = () => {
    input.classList.add("link-input--invalid");
    input.setAttribute("aria-invalid", "true");
    errorMessage.hidden = false;
    focusLinkInput(input);
  };

  if (options.initialUrl) {
    input.value = options.initialUrl;
  }

  input.addEventListener("input", clearValidationError);

  const applyLink = () => {
    if (isClosing) {
      return;
    }

    const url = input.value.trim();
    if (!url) {
      clearValidationError();
      if (options.allowUnset) {
        try {
          if (typeof options.onSave === "function") {
            options.onSave(null);
          } else {
            unsetEditorLink(editor);
          }
        } finally {
          closePopup();
        }
      } else {
        closePopup();
      }
      return;
    }

    const href = normalizeLinkHref(url);
    if (!href) {
      showValidationError();
      return;
    }

    clearValidationError();

    try {
      if (typeof options.onSave === "function") {
        options.onSave(href);
      } else {
        applyEditorLink(editor, href, { displayText: "link" });
      }
    } finally {
      closePopup();
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    event.stopPropagation();
    applyLink();
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closePopup();
    }
  });

  popup = tippy(document.body, {
    getReferenceClientRect: getRect,
    appendTo: () => document.body,
    content: component,
    showOnCreate: true,
    interactive: true,
    trigger: "manual",
    placement: "bottom-start",
    theme: "slash-menu",
    arrow: false,
    offset: [0, 8],
    maxWidth: "none",
    popperOptions: {
      strategy: "fixed",
    },
    onClickOutside() {
      closePopup();
    },
    onMount(instance) {
      instance.popper.style.zIndex = LINK_POPUP_Z_INDEX;
      const root = instance.popper.closest("[data-tippy-root]");
      if (root) {
        root.style.zIndex = LINK_POPUP_Z_INDEX;
      }
      focusLinkInput(input);
    },
  })[0];

  return popup;
}
